import { collect } from "../graph/paging.ts";
import { graphFetch, GraphError } from "../graph/client.ts";
import { DELETE_SCOPES } from "../auth/config.ts";
import { throwIfAborted } from "../export/abort.ts";
import { ownDeletableMessages } from "./own-messages.ts";
import type { ChatMessage } from "../modules/teams/types.ts";

export { ownDeletableMessages };

/**
 * Deleting a user's own data from their live Microsoft 365 account, via Graph.
 * The dangerous counterpart to the exporter — used only from the per-type
 * "Delete" action, only after a typed confirmation, and only on the containers
 * the user selected in the scan tree. It never touches the local export.
 *
 * Two hard limits come from Graph itself, not from choice:
 *  - Teams: only messages the signed-in user *sent* can be deleted (softDelete);
 *    other participants' messages are untouchable in delegated mode.
 *  - Mail: a built-in folder (Inbox, Sent Items, …) cannot be deleted, so its
 *    messages are removed but the folder itself stays.
 */

export interface DeleteResult {
    /** Items actually deleted (messages). */
    deleted: number;
    /** Items left alone (e.g. other people's Teams messages). */
    skipped: number;
    /** Mail folders removed. */
    foldersRemoved: number;
    errors: number;
}

export interface DeleteCallbacks {
    log: (message: string, kind: "info" | "ok" | "warn" | "err") => void;
    onProgress?: (deleted: number) => void;
    signal?: AbortSignal;
}

/** Soft-deletes the user's own messages in each selected chat. */
export async function deleteTeamsConversations(
    chatIds: readonly string[],
    selfUserId: string | undefined,
    cb: DeleteCallbacks
): Promise<DeleteResult> {
    const result: DeleteResult = { deleted: 0, skipped: 0, foldersRemoved: 0, errors: 0 };
    const signalOpt = cb.signal ? { signal: cb.signal } : {};

    for (const chatId of chatIds) {
        throwIfAborted(cb.signal);
        let messages: ChatMessage[];
        try {
            messages = await collect<ChatMessage>(`/chats/${chatId}/messages?$top=50`, {
                scopes: DELETE_SCOPES.teams,
                service: "outlook",
                ...signalOpt
            });
        } catch (err) {
            result.errors += 1;
            cb.log(`conversation listing failed: ${err instanceof GraphError ? err.toLogString() : String(err)}`, "err");
            continue;
        }

        const own = ownDeletableMessages(messages, selfUserId);
        result.skipped += messages.length - own.length;

        for (const message of own) {
            throwIfAborted(cb.signal);
            try {
                // chatMessage softDelete/undoSoftDelete live on the *beta*
                // endpoint only — calling it on v1.0 returns 405. graphFetch
                // uses an absolute URL as-is, so this reaches beta directly.
                await graphFetch(
                    `https://graph.microsoft.com/beta/me/chats/${chatId}/messages/${message.id}/softDelete`,
                    {
                        method: "POST",
                        scopes: DELETE_SCOPES.teams,
                        service: "outlook",
                        ...signalOpt
                    }
                );
                result.deleted += 1;
                cb.onProgress?.(result.deleted);
            } catch (err) {
                result.errors += 1;
                cb.log(`message delete failed: ${err instanceof GraphError ? err.toLogString() : String(err)}`, "err");
            }
        }
    }

    return result;
}

interface IdOnly {
    id: string;
}

/** Deletes every message in each selected folder, then removes the folder itself. */
export async function deleteMailFolders(
    folderIds: readonly string[],
    cb: DeleteCallbacks
): Promise<DeleteResult> {
    const result: DeleteResult = { deleted: 0, skipped: 0, foldersRemoved: 0, errors: 0 };
    const signalOpt = cb.signal ? { signal: cb.signal } : {};

    for (const folderId of folderIds) {
        throwIfAborted(cb.signal);

        let ids: IdOnly[];
        try {
            ids = await collect<IdOnly>(`/me/mailFolders/${folderId}/messages?$select=id&$top=100`, {
                scopes: DELETE_SCOPES.mail,
                service: "outlook",
                ...signalOpt
            });
        } catch (err) {
            // A folder deleted earlier this run (e.g. a parent cascaded it away).
            if (err instanceof GraphError && err.status === 404) {
                continue;
            }
            result.errors += 1;
            cb.log(`folder listing failed: ${err instanceof GraphError ? err.toLogString() : String(err)}`, "err");
            continue;
        }

        for (const { id } of ids) {
            throwIfAborted(cb.signal);
            try {
                await graphFetch(`/me/messages/${id}`, {
                    method: "DELETE",
                    scopes: DELETE_SCOPES.mail,
                    service: "outlook",
                    ...signalOpt
                });
                result.deleted += 1;
                cb.onProgress?.(result.deleted);
            } catch (err) {
                if (err instanceof GraphError && err.status === 404) {
                    continue; // already gone
                }
                result.errors += 1;
                cb.log(`message delete failed: ${err instanceof GraphError ? err.toLogString() : String(err)}`, "err");
            }
        }

        try {
            await graphFetch(`/me/mailFolders/${folderId}`, {
                method: "DELETE",
                scopes: DELETE_SCOPES.mail,
                service: "outlook",
                ...signalOpt
            });
            result.foldersRemoved += 1;
        } catch (err) {
            // Built-in folders (Inbox, Sent Items, …) can't be deleted — their
            // messages are gone, which is what matters. Anything else is logged.
            if (err instanceof GraphError && (err.status === 400 || err.status === 403)) {
                cb.log("a built-in folder was emptied but cannot itself be deleted", "warn");
            } else {
                result.errors += 1;
                cb.log(`folder delete failed: ${err instanceof GraphError ? err.toLogString() : String(err)}`, "err");
            }
        }
    }

    return result;
}
